import { Modal, Spinner } from '@skupreme/shared/ui';
import {
  providerStatus,
  timeAgo,
} from '@features/storage-providers/lib/provider-status';
import { useConnectionEvents } from '../use-rebuild-settings';

/** A health status in words: the connection statuses, and a stream's running / paused. */
export const healthLabel = (status: string) =>
  status === 'running' || status === 'paused'
    ? status.charAt(0).toUpperCase() + status.slice(1)
    : providerStatus(status).label;

/**
 * A connection's status history (newest first): each change of the
 * connection, or of one of its streams, with the reason the system wrote.
 */
export function ConnectionHistoryDialog({
  connectionId,
  name,
  onClose,
}: {
  connectionId: string | null;
  name: string;
  onClose: () => void;
}) {
  const { data, isLoading, isError } = useConnectionEvents(connectionId);
  const events = data?.v2 ? data.events : [];

  return (
    <Modal
      isOpen={!!connectionId}
      onOpenChange={open => !open && onClose()}
      title={`${name}: status history`}
      size="lg"
    >
      {isLoading ? (
        <Spinner />
      ) : isError ? (
        <p className="sk:text-fg-secondary">
          The history could not be read. Try again later.
        </p>
      ) : events.length === 0 ? (
        <p className="sk:text-fg-secondary">No status changes yet.</p>
      ) : (
        <ol className="sk:m-0 sk:flex sk:list-none sk:flex-col sk:gap-3 sk:p-0">
          {events.map(event => (
            <li
              key={`${event.at}-${event.stream ?? ''}-${event.to}`}
              className="sk:flex sk:flex-col sk:gap-0.5"
            >
              <span className="sk:text-dense sk:font-medium sk:text-fg">
                {event.stream ? `${event.stream}: ` : ''}
                {healthLabel(event.from)} → {healthLabel(event.to)}
                <span className="sk:font-normal sk:text-fg-tertiary">
                  {' '}
                  · {new Date(event.at).toLocaleString()} ({timeAgo(event.at)})
                </span>
              </span>
              <span className="sk:text-dense sk:text-fg-secondary sk:wrap-anywhere">
                {event.reason}
              </span>
            </li>
          ))}
        </ol>
      )}
    </Modal>
  );
}
